import React, { useEffect, useState } from 'react';
import { api, authHeaders } from '../api/client';
import { qrToDataUri } from './QrGenerator';

export function SuspiciousPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/manager/attendance/suspicious', authHeaders(token))
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="subtle">Loading suspicious entries...</p>;
  if (!items.length) return <p className="subtle">No suspicious entries found.</p>;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Employee</th>
            <th>Date</th>
            <th>Flag</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td>{item.employee_name || item.employee_code}</td>
              <td>{item.work_date}</td>
              <td><span className="status pending">{item.flag_type || 'suspicious'}</span></td>
              <td style={{ fontSize: '0.85em' }}>{item.detail || item.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AttendancePolicyPanel({ token }) {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/api/company-attendance/policy', authHeaders(token))
      .then((res) => setPolicy(res.data))
      .catch(() => setPolicy(null));
  }, [token]);

  async function savePolicy(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const res = await api.put('/api/company-attendance/policy', policy, authHeaders(token));
      setPolicy(res.data);
      setMsg('Policy saved.');
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  if (!policy) return <p className="subtle">Loading policy...</p>;

  return (
    <form onSubmit={savePolicy} className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
      <label>
        Rollout Mode
        <select value={policy.rollout_mode || 'none'} onChange={(e) => setPolicy({ ...policy, rollout_mode: e.target.value })}>
          <option value="none">None (manual OK)</option>
          <option value="pilot">Pilot (selected employees)</option>
          <option value="all">All (secure required)</option>
        </select>
      </label>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={policy.require_gps || false} onChange={(e) => setPolicy({ ...policy, require_gps: e.target.checked })} />
        Require GPS
      </label>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={policy.require_face || false} onChange={(e) => setPolicy({ ...policy, require_face: e.target.checked })} />
        Require Face
      </label>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={policy.require_qr || false} onChange={(e) => setPolicy({ ...policy, require_qr: e.target.checked })} />
        Require QR
      </label>
      <label>
        Max GPS Accuracy (m)
        <input type="number" value={policy.max_gps_accuracy_m || 50} onChange={(e) => setPolicy({ ...policy, max_gps_accuracy_m: Number(e.target.value) })} style={{ width: 80 }} />
      </label>
      <label>
        Min Face Match
        <input type="number" step="0.01" value={policy.min_face_match_score || 0.7} onChange={(e) => setPolicy({ ...policy, min_face_match_score: Number(e.target.value) })} style={{ width: 80 }} />
      </label>
      <div style={{ width: '100%' }}>
        <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Policy'}</button>
        {msg ? <span className="subtle" style={{ marginLeft: 10 }}>{msg}</span> : null}
      </div>
    </form>
  );
}

export function GeofenceSitesPanel({ token }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSite, setNewSite] = useState({ name: '', latitude: '', longitude: '', radius_m: 100 });
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/api/company-attendance/sites', authHeaders(token))
      .then((res) => setSites(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, [token]);

  async function addSite(e) {
    e.preventDefault();
    setMsg('');
    try {
      const res = await api.post('/api/company-attendance/sites', {
        name: newSite.name,
        latitude: Number(newSite.latitude),
        longitude: Number(newSite.longitude),
        radius_m: Number(newSite.radius_m),
      }, authHeaders(token));
      setSites([...sites, res.data]);
      setNewSite({ name: '', latitude: '', longitude: '', radius_m: 100 });
      setMsg('Site added.');
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Failed to add site.');
    }
  }

  return (
    <div>
      {loading ? <p className="subtle">Loading sites...</p> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Lat</th><th>Lng</th><th>Radius</th><th>Active</th></tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.latitude?.toFixed(5)}</td>
                  <td>{s.longitude?.toFixed(5)}</td>
                  <td>{s.radius_m}m</td>
                  <td>{s.is_active ? '✓' : '—'}</td>
                </tr>
              ))}
              {!sites.length ? <tr><td colSpan={5} className="subtle">No sites configured.</td></tr> : null}
            </tbody>
          </table>
        </div>
      )}
      <form onSubmit={addSite} className="row" style={{ marginTop: 10, gap: 8 }}>
        <input placeholder="Site name" value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })} required />
        <input placeholder="Latitude" type="number" step="any" value={newSite.latitude} onChange={(e) => setNewSite({ ...newSite, latitude: e.target.value })} required style={{ width: 100 }} />
        <input placeholder="Longitude" type="number" step="any" value={newSite.longitude} onChange={(e) => setNewSite({ ...newSite, longitude: e.target.value })} required style={{ width: 100 }} />
        <input placeholder="Radius (m)" type="number" value={newSite.radius_m} onChange={(e) => setNewSite({ ...newSite, radius_m: e.target.value })} style={{ width: 80 }} />
        <button type="submit">Add Site</button>
      </form>
      {msg ? <p className="subtle" style={{ marginTop: 6 }}>{msg}</p> : null}
    </div>
  );
}

export function KioskPanel({ token }) {
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/api/company-attendance/kiosks', authHeaders(token))
      .then((res) => setKiosks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setKiosks([]))
      .finally(() => setLoading(false));
  }, [token]);

  async function registerKiosk() {
    setMsg('');
    try {
      const res = await api.post('/api/company-attendance/kiosks/register', { name: `Kiosk ${kiosks.length + 1}` }, authHeaders(token));
      setKiosks([...kiosks, res.data]);
      setMsg(`Registered. API Key: ${res.data.api_key || '(see response)'}`);
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Failed to register.');
    }
  }

  return (
    <div>
      {loading ? <p className="subtle">Loading kiosks...</p> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Site</th><th>Status</th><th>Last Seen</th></tr>
            </thead>
            <tbody>
              {kiosks.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>{k.site_name || k.site_id || '—'}</td>
                  <td><span className={`status ${k.is_active ? 'approved' : 'pending'}`}>{k.is_active ? 'active' : 'inactive'}</span></td>
                  <td>{k.last_heartbeat ? new Date(k.last_heartbeat).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {!kiosks.length ? <tr><td colSpan={4} className="subtle">No kiosks registered.</td></tr> : null}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <button onClick={registerKiosk}>Register New Kiosk</button>
        {msg ? <span className="subtle" style={{ marginLeft: 10 }}>{msg}</span> : null}
      </div>
    </div>
  );
}

export function QrChallengePanel({ token }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function generateQr() {
    setLoading(true);
    try {
      const res = await api.post('/api/self/attendance/qr-challenge', {}, authHeaders(token));
      setQrData(res.data);
    } catch {
      setQrData(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <button onClick={generateQr} disabled={loading}>{loading ? 'Generating...' : 'Generate QR Code'}</button>
      {qrData ? (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <img src={qrToDataUri(qrData.qr_token || qrData.token || '')} alt="QR Code" style={{ width: 200, height: 200 }} />
          <p className="subtle" style={{ marginTop: 6 }}>Scan this with a kiosk or Expo Go app.</p>
        </div>
      ) : null}
    </div>
  );
}
